// SystemPanel.tsx — Detail panel for the focused star system. With no planet
// focused it lists the system's planet roster (each row clickable to focus that
// planet); once a planet is focused it switches to a planet sheet (kind, orbit,
// dynamics, climate, satellites). The back button steps out one level — to the
// system from a planet, else to the galaxy.
import type { Galaxy, Moon, Planet, StarSystem } from '../types';
import { moonName, planetModel } from '../scene/planet/model';
import { cityList } from '../scene/planet/cities';
import { gatesOf, HYPERLANE_SPEED_C, type Gate } from '../scene/hyperlane';
import { useMatch, useSim } from '../state/context';
import { ownerColor } from '../state/economy';
import { SendHere, WorldSection } from './Empire';
import { FOOD_PER_POP_PER_DAY, RESOURCES, type PopGroup } from '../state/economy';

// How many cities to list individually before collapsing the tail into a summary
// row — keeps a 50-city megaworld's sheet readable.
const CITY_LIST_LIMIT = 10;

interface Props {
  galaxy: Galaxy;
  system: StarSystem;
  focusedPlanetId: string | null;
  focusedMoonId: string | null;
  selectedSingularityId: string | null;
  onSelectPlanet: (id: string) => void;
  onSelectMoon: (id: string) => void;
  onSelectSingularity: (id: string | null) => void;
  /** Travel down a hyperlane: focus the destination system. */
  onTravel: (id: string) => void;
  onBack: () => void;
}

function deg(rad: number): string {
  return `${((rad * 180) / Math.PI).toFixed(1)}°`;
}

// Periods are stored in game-seconds, which at 1× (realtime) are calendar seconds;
// show whichever of hours / days / months / years keeps the number readable.
const HOUR = 3600;
const DAY = 86400;
const MONTH = 2629800; // 30.44 days
const YEAR = 31557600; // 365.25 days

function formatPeriod(sec: number): string {
  if (sec < DAY) return `${(sec / HOUR).toFixed(1)} hr`;
  if (sec < 2 * MONTH) return `${(sec / DAY).toFixed(0)} days`;
  if (sec < YEAR) return `${(sec / MONTH).toFixed(1)} months`;
  return `${(sec / YEAR).toFixed(2)} yr`;
}

// Planet orbits are generated in compressed scene units. The orbit law (ORBIT_K in
// generate.ts) is calibrated so a≈2.5 units yields a ~1-year period, which for a
// sun-like star is 1 AU by Kepler's third law — so 2.5 scene units ≈ 1 AU. This
// keeps the displayed orbit radius consistent with the displayed period. (Moons
// orbit far closer and are shown in raw units, not AU.)
const SCENE_UNITS_PER_AU = 2.5;

function formatAu(sceneUnits: number): string {
  return `${(sceneUnits / SCENE_UNITS_PER_AU).toFixed(2)} AU`;
}

function formatDayLength(hours: number | null): string {
  if (hours === null) return 'synchronous';
  if (hours < 48) return `${hours.toFixed(1)} h`;
  return `${(hours / 24).toFixed(1)} days`;
}

function formatTemp(c: number): string {
  return `${c}°C`;
}

// Pops are millions of people (1 pop = 1,000,000). Show the game-native pop count
// plus a humanized head-count, e.g. "10,000 pops · 10B".
function formatPops(pops: number): string {
  const people = pops * 1e6;
  const human =
    people >= 1e9 ? `${(people / 1e9).toFixed(people >= 1e10 ? 0 : 1)}B` : `${Math.round(people / 1e6)}M`;
  return `${pops.toLocaleString()} pops · ${human}`;
}

// Days of food a cohort has left at its current consumption — the survival metric the
// server's daily tick decrements (server/sim/sim.go). Below ~5 the cohort shrinks.
function foodDays(c: PopGroup): number {
  const daily = c.count * FOOD_PER_POP_PER_DAY;
  return daily > 0 ? c.foodReserve / daily : 0;
}

// Compact goods quantity (stockpiles run to the hundreds of thousands).
function formatQty(n: number): string {
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return n.toFixed(0);
}

// Cohort treasury (credits) — can go negative (debt) once money-gated trade bites.
function formatMoney(n: number): string {
  return `${n < 0 ? '−' : ''}§${formatQty(Math.abs(n))}`;
}

function PlanetSheet({
  planet,
  star,
  onSelectMoon,
  onBack,
}: {
  planet: Planet;
  star: StarSystem;
  onSelectMoon: (id: string) => void;
  onBack: () => void;
}) {
  const model = planetModel(planet);
  const climate = model.climate(star);
  const moons = planet.moons ?? [];
  const pops = planet.pops ?? 0;
  const cities = cityList(planet);
  // Live economy on this world (server-authoritative), if any. The scoped snapshot's
  // arrays can be absent (the focused system has no economy, e.g. an uninhabited system),
  // so default them — JSON nulls would otherwise throw here.
  const sim = useSim();
  const cohorts = (sim?.pops ?? []).filter((c) => c.planetId === planet.id);
  const stockpile = (sim?.stockpiles ?? []).find((s) => s.planetId === planet.id);
  const contracts = (sim?.contracts ?? []).filter((ct) => ct.planetId === planet.id);
  const match = useMatch();
  return (
    <aside className="system-panel">
      <button className="back-btn" onClick={onBack}>
        ← System
      </button>
      <h1>{planet.name}</h1>
      <div className="spectral">{model.label}</div>
      {match ? <WorldSection planet={planet} /> : null}
      {match ? <div className="planet-count">World</div> : null}
      <dl className="stat-list">
        {match ? null : (
          <div className="stat">
            <dt>Population</dt>
            <dd>{pops > 0 ? formatPops(pops) : model.habitable ? 'Uninhabited' : 'Uninhabitable'}</dd>
          </div>
        )}
        {pops > 0 && !match ? (
          <div className="stat">
            <dt>Cities</dt>
            <dd>{cities.length}</dd>
          </div>
        ) : null}
        <div className="stat">
          <dt>Orbit radius</dt>
          <dd>{formatAu(planet.semiMajorAxis)}</dd>
        </div>
        <div className="stat">
          <dt>Orbital period</dt>
          <dd>{formatPeriod(planet.period)}</dd>
        </div>
        <div className="stat">
          <dt>Day length</dt>
          <dd>{formatDayLength(climate.dayLengthHours)}</dd>
        </div>
        <div className="stat">
          <dt>Mean temp</dt>
          <dd>{formatTemp(climate.meanTempC)}</dd>
        </div>
        <div className="stat">
          <dt>Day / night</dt>
          <dd>
            {formatTemp(climate.dayTempC)} / {formatTemp(climate.nightTempC)}
          </dd>
        </div>
        <div className="stat">
          <dt>Eccentricity</dt>
          <dd>{planet.eccentricity.toFixed(3)}</dd>
        </div>
        <div className="stat">
          <dt>Inclination</dt>
          <dd>{deg(planet.inclination)}</dd>
        </div>
        <div className="stat">
          <dt>Rotation</dt>
          <dd>{planet.tidallyLocked ? 'Tidally locked' : 'Free'}</dd>
        </div>
        <div className="stat">
          <dt>Rings</dt>
          <dd>{planet.rings ? 'Yes' : 'None'}</dd>
        </div>
        <div className="stat">
          <dt>Moons</dt>
          <dd>{moons.length}</dd>
        </div>
        {planet.deposits ? (
          <>
            <div className="stat">
              <dt>Fertility</dt>
              <dd>{(planet.deposits.fertility ?? 0).toFixed(2)}×</dd>
            </div>
            <div className="stat">
              <dt>Ore deposit</dt>
              <dd>{(planet.deposits.ore ?? 0).toFixed(2)}×</dd>
            </div>
          </>
        ) : null}
      </dl>
      {cohorts.length > 0 ? (
        <>
          <div className="planet-count">Workforce</div>
          <ul className="moon-list">
            {cohorts.map((c) => (
              <li key={c.id} className="moon-row" style={{ cursor: 'default' }}>
                <span className="dot" style={{ background: '#7fe0c0', color: '#7fe0c0' }} />
                <span className="mname">{c.job}</span>
                <span className="mmeta">
                  {Math.round(c.count).toLocaleString()} pops · {foodDays(c).toFixed(0)}d · {formatMoney(c.money)}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {stockpile ? (
        <>
          <div className="planet-count">Market</div>
          <dl className="stat-list">
            {RESOURCES.map((g) => (
              <div className="stat" key={g}>
                <dt style={{ textTransform: 'capitalize' }}>{g}</dt>
                <dd>{formatQty(stockpile.goods[g] ?? 0)}</dd>
              </div>
            ))}
          </dl>
        </>
      ) : null}
      {contracts.length > 0 ? (
        <>
          <div className="planet-count">Supply contracts ({contracts.length})</div>
          <ul className="moon-list">
            {contracts.slice(0, 8).map((ct) => (
              <li key={ct.id} className="moon-row" style={{ cursor: 'default' }}>
                <span className="dot" style={{ background: '#9ab0d6', color: '#9ab0d6' }} />
                <span className="mname" style={{ textTransform: 'capitalize' }}>{ct.good}</span>
                <span className="mmeta">§{ct.price.toFixed(2)}/u</span>
              </li>
            ))}
            {contracts.length > 8 ? (
              <li className="moon-row" style={{ cursor: 'default', opacity: 0.6 }}>
                <span className="mname">+{contracts.length - 8} more</span>
              </li>
            ) : null}
          </ul>
        </>
      ) : null}
      {cities.length > 0 ? (
        <>
          <div className="planet-count">Cities</div>
          <ul className="moon-list">
            {cities.slice(0, CITY_LIST_LIMIT).map((c, i) => (
              <li key={i} className="moon-row" style={{ cursor: 'default' }}>
                <span className="dot" style={{ background: '#ffce85', color: '#ffce85' }} />
                <span className="mname">{c.name}</span>
                <span className="mmeta">{formatPops(c.pops)}</span>
              </li>
            ))}
            {cities.length > CITY_LIST_LIMIT ? (
              <li className="moon-row" style={{ cursor: 'default', opacity: 0.6 }}>
                <span className="mname">+{cities.length - CITY_LIST_LIMIT} more settlements</span>
              </li>
            ) : null}
          </ul>
        </>
      ) : null}
      {moons.length > 0 ? (
        <ul className="moon-list">
          {moons.map((m, i) => (
            <li key={m.id} className="moon-row" onClick={() => onSelectMoon(m.id)}>
              <span className="dot" style={{ background: m.color, color: m.color }} />
              <span className="mname">{moonName(planet.name, i)}</span>
              <span className="mmeta">{m.tidallyLocked ? 'locked' : 'free'}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </aside>
  );
}

// Detail sheet for a focused moon: its orbit around the parent planet and dynamics.
function MoonSheet({ moon, name, onBack }: { moon: Moon; name: string; onBack: () => void }) {
  return (
    <aside className="system-panel">
      <button className="back-btn" onClick={onBack}>
        ← Planet
      </button>
      <h1>{name}</h1>
      <div className="spectral">Moon</div>
      <dl className="stat-list">
        <div className="stat">
          <dt>Orbit radius</dt>
          <dd>{moon.semiMajorAxis.toFixed(2)}</dd>
        </div>
        <div className="stat">
          <dt>Orbital period</dt>
          <dd>{formatPeriod(moon.period)}</dd>
        </div>
        <div className="stat">
          <dt>Eccentricity</dt>
          <dd>{moon.eccentricity.toFixed(3)}</dd>
        </div>
        <div className="stat">
          <dt>Inclination</dt>
          <dd>{deg(moon.inclination)}</dd>
        </div>
        <div className="stat">
          <dt>Rotation</dt>
          <dd>{moon.tidallyLocked ? 'Tidally locked' : 'Free'}</dd>
        </div>
      </dl>
    </aside>
  );
}

// Detail sheet for a selected point of singularity: where the lane goes, how far,
// and how long the crossing takes at hyperlane speed. The destination is a jump
// button that flies the camera to that system.
function GateSheet({
  gate,
  onTravel,
  onBack,
}: {
  gate: Gate;
  onTravel: (id: string) => void;
  onBack: () => void;
}) {
  return (
    <aside className="system-panel">
      <button className="back-btn" onClick={onBack}>
        ← System
      </button>
      <h1>Singularity</h1>
      <div className="spectral">Hyperlane gate</div>
      <dl className="stat-list">
        <div className="stat">
          <dt>Destination</dt>
          <dd>{gate.neighbor.name}</dd>
        </div>
        <div className="stat">
          <dt>Star class</dt>
          <dd>{gate.neighbor.spectralClass}-class</dd>
        </div>
        <div className="stat">
          <dt>Distance</dt>
          <dd>{gate.lightYears.toFixed(1)} ly</dd>
        </div>
        <div className="stat">
          <dt>Lane speed</dt>
          <dd>{HYPERLANE_SPEED_C}× c</dd>
        </div>
        <div className="stat">
          <dt>Transit time</dt>
          <dd>{formatPeriod(gate.transitSeconds)}</dd>
        </div>
      </dl>
      <button className="travel-btn" onClick={() => onTravel(gate.neighbor.id)}>
        Jump to {gate.neighbor.name} →
      </button>
    </aside>
  );
}

export function SystemPanel({
  galaxy,
  system,
  focusedPlanetId,
  focusedMoonId,
  selectedSingularityId,
  onSelectPlanet,
  onSelectMoon,
  onSelectSingularity,
  onTravel,
  onBack,
}: Props) {
  // Hooks must run before any early return below. The trade hub (broker) is system-level.
  const sim = useSim();
  const match = useMatch();
  const broker = (sim?.brokers ?? []).find((b) => b.systemId === system.id);
  const focusedPlanet = focusedPlanetId
    ? system.planets.find((p) => p.id === focusedPlanetId) ?? null
    : null;
  // A focused moon lives in the focused planet; resolve it + its display name.
  let focusedMoon: { moon: Moon; name: string } | null = null;
  if (focusedMoonId && focusedPlanet) {
    const i = (focusedPlanet.moons ?? []).findIndex((m) => m.id === focusedMoonId);
    if (i >= 0) {
      focusedMoon = { moon: focusedPlanet.moons[i], name: moonName(focusedPlanet.name, i) };
    }
  }
  const gates = gatesOf(galaxy, system.id);
  const selectedGate = selectedSingularityId
    ? gates.find((g) => g.id === selectedSingularityId) ?? null
    : null;

  // A focused moon is deeper than its (also-focused) parent planet, so check it first.
  if (focusedMoon) {
    return <MoonSheet moon={focusedMoon.moon} name={focusedMoon.name} onBack={onBack} />;
  }

  if (focusedPlanet) {
    return <PlanetSheet planet={focusedPlanet} star={system} onSelectMoon={onSelectMoon} onBack={onBack} />;
  }

  if (selectedGate) {
    return <GateSheet gate={selectedGate} onTravel={onTravel} onBack={onBack} />;
  }

  return (
    <aside className="system-panel">
      <button className="back-btn" onClick={onBack}>
        ← Galaxy
      </button>
      <h1>{system.name}</h1>
      <div className="spectral">{system.spectralClass}-class star</div>
      <div className="planet-count">
        {system.planets.length} {system.planets.length === 1 ? 'planet' : 'planets'}
      </div>
      <ul className="planet-list">
        {system.planets.map((p) => {
          const world = match ? (sim?.worlds ?? []).find((w) => w.planetId === p.id) : undefined;
          return (
          <li key={p.id} className="planet-row" onClick={() => onSelectPlanet(p.id)}>
            <span className="dot" style={{ background: p.color, color: p.color }} />
            <span className="pname">
              {world ? (
                <span
                  className="owner-pip"
                  title={world.owner ? 'Empire world' : 'Independent world'}
                  style={{ background: world.owner ? ownerColor(sim, world.owner) : 'transparent', borderColor: ownerColor(sim, world.owner) }}
                />
              ) : null}
              {p.name}
              {(p.pops ?? 0) > 0 ? (
                <span title="Inhabited" style={{ color: '#ffce85', marginLeft: 6 }}>
                  ⌂
                </span>
              ) : null}
            </span>
            <span className="pkind">{planetModel(p).label}</span>
            {match ? <SendHere planetId={p.id} compact /> : null}
          </li>
          );
        })}
        {system.planets.length === 0 ? <li className="empty">No charted planets</li> : null}
      </ul>
      {gates.length > 0 ? (
        <>
          <div className="planet-count">
            {gates.length} {gates.length === 1 ? 'hyperlane' : 'hyperlanes'}
          </div>
          <ul className="planet-list gate-list">
            {gates.map((g) => (
              <li
                key={g.id}
                className={g.id === selectedSingularityId ? 'planet-row active' : 'planet-row'}
                onClick={() => onSelectSingularity(g.id)}
              >
                <span className="gate-mark">◐</span>
                <span className="pname">{g.neighbor.name}</span>
                <span className="pkind">{formatPeriod(g.transitSeconds)}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {broker ? (
        <>
          <div className="planet-count">Trade hub</div>
          <dl className="stat-list">
            <div className="stat">
              <dt>Broker capital</dt>
              <dd>{formatMoney(broker.money)}</dd>
            </div>
            <div className="stat">
              <dt>Trading profit</dt>
              <dd>{formatMoney(broker.profit)}</dd>
            </div>
          </dl>
          {Object.entries(broker.inventory).filter(([, q]) => q > 0).length > 0 ? (
            <ul className="moon-list">
              {Object.entries(broker.inventory)
                .filter(([, q]) => q > 0)
                .map(([g, q]) => (
                  <li key={g} className="moon-row" style={{ cursor: 'default' }}>
                    <span className="dot" style={{ background: '#caa46a', color: '#caa46a' }} />
                    <span className="mname" style={{ textTransform: 'capitalize' }}>
                      {g} in transit
                    </span>
                    <span className="mmeta">{formatQty(q)}</span>
                  </li>
                ))}
            </ul>
          ) : null}
        </>
      ) : null}
    </aside>
  );
}
